import { isCourseFamilyAvailableIn } from "@/config/courseStateMapping";
import { useLocation } from "@/contexts/LocationContext";
import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface FAQItem {
  question: string;
  answer: string;
  isKeyword?: boolean;
}

interface SEOFAQSectionProps {
  title?: string;
  centreSpecific?: boolean;
  centreName?: string;
  faqs?: FAQItem[];
}

const SEOFAQSection: React.FC<SEOFAQSectionProps> = ({ 
  title = "Frequently Asked Questions",
  centreSpecific = false,
  centreName = "",
  faqs = []
}) => {
  const { selectedState } = useLocation();
  const defaultFAQs: FAQItem[] = [
    {
      question: "What age groups do you teach?",
      answer: "We specialise in students from Kindergarten, Prep or Pre-primary through to Year 8. Our primary programmes cover Foundations, NAPLAN, and state-specific pathways like OC, Selective School entry, or Scholarship preparation. For Year 7 and Year 8 students, we offer Maths and English foundations to support the transition to high school.",
      isKeyword: true
    },
    {
      question: centreSpecific 
        ? `Do you offer in-person tutoring in ${centreName}?`
        : "Do you offer in-person tutoring?",
      answer: centreSpecific
        ? `Yes, we offer premium in-centre tutoring at our ${centreName} location. We also provide online tutoring options for flexibility. Students can choose between face-to-face instruction at our centre or interactive online sessions.`
        : "Yes, we offer both in-centre and online tutoring options. Our centres provide face-to-face instruction with qualified teachers, while our online platform offers interactive virtual learning.",
      isKeyword: true
    },
    {
      question: "What subjects do you cover for primary students?",
      answer: "We offer comprehensive tutoring in Mathematics, English, Reading Comprehension, Writing, and General Ability. Our programmes include NAPLAN preparation, Foundations programmes, and state-specific entrance preparation where available. For Year 7 and Year 8 students, we also offer Maths and English foundations programmes.",
      isKeyword: true
    },
    {
      question: "What is your teaching methodology?",
      answer: "We use our proven TMRW™ methodology, which combines targeted practice, strategic thinking, and comprehensive exam preparation. Our approach focuses on building both foundational skills and advanced problem-solving abilities.",
      isKeyword: true
    },
    {
      question: "How small are your class sizes?",
      answer: "We maintain small class sizes with a maximum of 8 students per class. This ensures personalised attention and allows our qualified teachers to focus on each student's individual learning needs.",
      isKeyword: true
    },
    {
      question: centreSpecific 
        ? `What programmes are available at ${centreName}?`
        : "What programmes do you offer?",
      answer: "We offer Foundations and NAPLAN Preparation, plus state-specific programs like OC, Selective School entry, or GATE preparation for primary students (K-6), plus Year 7-8 Maths and English foundations. All programmes are available both in-centre and online.",
      isKeyword: true
    },
    {
      question: "How do I book an assessment?",
      answer: "You can book a diagnostic assessment through our online booking system. This helps us understand your child's current level and recommend the most suitable programme. Contact us to schedule your assessment.",
      isKeyword: true
    },
    {
      question: "Are your teachers qualified?",
      answer: "Yes, all our teachers are qualified NSW, VIC and WA curriculum teachers with specific experience in primary and early secondary education (Kindergarten, Prep or Pre-primary to Year 8). They are trained in our TMRW™ methodology and specialise in working with students from Kindergarten, Prep or Pre-primary to Year 8.",
      isKeyword: true
    }
  ];

  const allFAQs = faqs.length > 0 ? faqs : defaultFAQs;

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold text-foreground mb-4">
              {title}
            </CardTitle>
            <p className="text-muted-foreground">
              {centreSpecific 
                ? `Common questions about our ${centreName} tutoring programs`
                : "Everything you need to know about our primary tutoring programs"
              }
            </p>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {allFAQs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left hover:text-primary">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
      </div>

      {/* Schema markup for FAQ */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": allFAQs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        })}
      </script>
    </section>
  );
};

export default SEOFAQSection;